import { useCallback, useRef, useState } from 'react';
import { createConsumer, type Subscription } from '@rails/actioncable';
import { captureException } from '@sentry/react';
import type { RecommendedPlanActivity } from '@/data/idp/profile-based-recommendations/interfaces';
import { getAuthToken } from '@/utils/auth-token';

export enum RecommendationStreamingEvent {
  Started = 'started',
  TextChunk = 'text_chunk',
  Activity = 'activity',
  Completed = 'completed',
  StreamEnded = 'stream_ended',
  Error = 'error',
}

interface UseIdpRecommendationStreamingOptions {
  onCompleted: (
    activities: RecommendedPlanActivity[],
    planDescription: string,
    summary: string
  ) => void;
  onError?: (message: string) => void;
}

interface ReceivedData extends Record<string, unknown> {
  event: RecommendationStreamingEvent;
}

export const useIdpRecommendationStreaming = ({
  onCompleted,
  onError,
}: UseIdpRecommendationStreamingOptions) => {
  const [status, setStatus] = useState<RecommendationStreamingEvent>(
    RecommendationStreamingEvent.Started
  );
  const [activities, setActivities] = useState<RecommendedPlanActivity[]>([]);
  const [planDescription, setPlanDescription] = useState('');

  const subscriptionRef = useRef<Subscription | null>(null);
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null);
  const activitiesRef = useRef<RecommendedPlanActivity[]>([]);
  const planDescriptionRef = useRef('');
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);

  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;

  const disconnect = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
    consumerRef.current?.disconnect();
    consumerRef.current = null;
  }, []);

  const startStreaming = useCallback(
    (recommendationId: string) => {
      disconnect();

      activitiesRef.current = [];
      planDescriptionRef.current = '';
      setActivities([]);
      setPlanDescription('');
      setStatus(RecommendationStreamingEvent.Started);

      const token = getAuthToken();
      const sessionId = `idp_${recommendationId}_${Date.now()}`;

      consumerRef.current = createConsumer(`/cable?token=${token}`);
      subscriptionRef.current = consumerRef.current.subscriptions.create(
        { channel: 'IDP::Channel::Recommendation', session_id: sessionId },
        {
          connected() {
            subscriptionRef.current?.perform('generate', {
              profile_based_recommendation_id: recommendationId,
            });
          },
          received(data: ReceivedData) {
            const eventHandlers: Record<
              RecommendationStreamingEvent,
              () => void
            > = {
              started: () => setStatus(RecommendationStreamingEvent.TextChunk),
              text_chunk: () => {
                if (data.content) {
                  planDescriptionRef.current += data.content;
                  setPlanDescription(planDescriptionRef.current);
                }
              },
              activity: () => {
                if (data.activity) {
                  activitiesRef.current = [
                    ...activitiesRef.current,
                    data.activity as RecommendedPlanActivity,
                  ];
                  setActivities([...activitiesRef.current]);
                }
              },
              completed: () => {
                const summary =
                  typeof data.summary === 'string' ? data.summary : '';
                setStatus(RecommendationStreamingEvent.Completed);
                disconnect();
                onCompletedRef.current(
                  activitiesRef.current,
                  planDescriptionRef.current,
                  summary
                );
              },
              stream_ended: () => disconnect(),
              error: () => {
                const message =
                  typeof data.message === 'string'
                    ? data.message
                    : 'Unknown error';
                setStatus(RecommendationStreamingEvent.Error);
                disconnect();
                onErrorRef.current?.(message);
                captureException(new Error(`IDP streaming error: ${message}`));
              },
            };

            eventHandlers[data.event]?.();
          },
          rejected() {
            setStatus(RecommendationStreamingEvent.Error);
            onErrorRef.current?.('Connection rejected');
          },
        }
      );
    },
    [disconnect]
  );

  return {
    status,
    activities,
    planDescription,
    startStreaming,
  };
};
